import { DataGridPage, DataGridPageQueryParams } from '@if/mui'
import { useInteracWireHistory } from '@src/api/interac-history-api'
import { EInteracWireStatus, InteracWire, InteracWireHistoryFiltersType } from '@src/types/interac'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'
import { useDeletedInteracWireColumns } from './DeletedInteracWire-hook'
import {
  getInteracHistoryStatusList,
  getSearchFilters,
  interacHistoryFiltersSchema,
  InteracHistoryFiltersType,
} from './InteracHistory-schema'
import { useRecordedInteracWireColumns } from './RecordedInteracWire-hook'

// The endpoint returns no total count, so the library's pager works off a has-next heuristic
// (a full page implies another one) and the page size stays what the old footer offered.
const DEFAULT_LIMIT = 15
const PAGE_SIZE_OPTIONS = [10, 15, 25]

/** The history query, keyed on the page's URL state. */
const useInteracWireHistoryData = ({
  filters,
  pagination,
}: DataGridPageQueryParams<InteracWire, InteracHistoryFiltersType>) => {
  const historyFilters = useMemo<InteracWireHistoryFiltersType>(
    () => ({
      // Anything but the deleted tab is the recorded one — the endpoint serves no other status.
      status:
        filters.status === EInteracWireStatus.Discarded ? EInteracWireStatus.Discarded : EInteracWireStatus.Matched,
      ...getSearchFilters(filters),
      limit: pagination.limit,
      offset: pagination.offset,
    }),
    [filters, pagination],
  )

  const [data, isLoading] = useInteracWireHistory(historyFilters)
  return { data, isLoading }
}

const InteracHistoryList = () => {
  const { t } = useTranslation()
  const recordedColumns = useRecordedInteracWireColumns()
  const deletedColumns = useDeletedInteracWireColumns()

  // Each tab keeps its own columns, and `columns` is a prop of `DataGridPage` rather than
  // something one of its slots can derive — so the active tab is read from the URL, which is
  // the filters' source of truth either way.
  const [searchParams] = useSearchParams()
  const status = useMemo(
    () =>
      interacHistoryFiltersSchema.cast(Object.fromEntries(searchParams), { stripUnknown: true, assert: false }).status,
    [searchParams],
  )

  const columns = status === EInteracWireStatus.Discarded ? deletedColumns : recordedColumns

  const tabs = useMemo(
    () =>
      getInteracHistoryStatusList().map((tabStatus) => ({
        value: tabStatus,
        label: t(`enum.interacHistoryTab.${tabStatus}`),
      })),
    [t],
  )

  return (
    <DataGridPage
      schema={interacHistoryFiltersSchema}
      useData={useInteracWireHistoryData}
      columns={columns}
      tabsField="status"
      tabs={tabs}
      searchPlaceholder={t('interacHistory.searchPlaceholder')}
      defaultLimit={DEFAULT_LIMIT}
      canChangePageSize
      pageSizeOptions={PAGE_SIZE_OPTIONS}
    />
  )
}

export default InteracHistoryList
